import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useChatSocket } from "@/hooks/useChatSocket";

/**
 * SocialContext — single source of truth for social signals shared across
 * the app: pending friend-request count, total unread chat messages,
 * per-conversation unread map, and social toast queue (friend_request /
 * friend_accepted).
 *
 * All of these previously lived in separate hooks/components (each opening
 * its own WebSocket). SocialProvider mounts ONE `useChatSocket` listener
 * and fans-out to consumers, cutting redundant WS traffic and duplicated
 * `/friends/requests` polling.
 */

const SocialContext = createContext(null);

/** Short "ding" via Web Audio API. Silent if disabled via localStorage or unsupported. */
function playDing() {
  try {
    if (typeof localStorage !== "undefined" && localStorage.getItem("yappo-notif-sound") === "0") return;
    const Ctor = window.AudioContext || window.webkitAudioContext;
    if (!Ctor) return;
    const ctx = new Ctor();
    const now = ctx.currentTime;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = "sine";
    osc.frequency.setValueAtTime(880, now);
    osc.frequency.exponentialRampToValueAtTime(520, now + 0.18);
    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.exponentialRampToValueAtTime(0.18, now + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.32);
    osc.connect(gain).connect(ctx.destination);
    osc.start(now);
    osc.stop(now + 0.35);
    setTimeout(() => { try { ctx.close(); } catch {} }, 500);
  } catch {}
}

export function SocialProvider({ children }) {
  const { user } = useAuth() || {};
  const [pendingRequestCount, setPendingRequestCount] = useState(0);
  const [unreadByConv, setUnreadByConv] = useState({}); // { convId: number }
  const [toasts, setToasts] = useState([]); // {id, type, title, subtitle, actionLabel, target, userId}
  const idRef = useRef(0);

  // Active conversation ID (chat that user is currently viewing). Messages
  // arriving for this conv should NOT bump the unread badge.
  const activeConvIdRef = useRef(null);
  const setActiveConversation = useCallback((convId) => {
    activeConvIdRef.current = convId || null;
    if (convId) {
      setUnreadByConv((prev) => {
        if (!prev[convId]) return prev;
        const next = { ...prev };
        delete next[convId];
        return next;
      });
    }
  }, []);

  // --------------------------- Fetchers -----------------------------------
  const refetchFriends = useCallback(async () => {
    if (!user) { setPendingRequestCount(0); return; }
    try {
      const { data } = await api.get("/friends/requests");
      setPendingRequestCount((data?.incoming || []).length);
    } catch { /* keep prior on transient errors */ }
  }, [user]);

  const refetchUnread = useCallback(async () => {
    if (!user) { setUnreadByConv({}); return; }
    try {
      const { data } = await api.get("/conversations");
      const map = {};
      for (const c of data || []) {
        const n = c.unread_count || 0;
        if (n > 0 && c.id !== activeConvIdRef.current) map[c.id] = n;
      }
      setUnreadByConv(map);
    } catch {}
  }, [user]);

  useEffect(() => {
    refetchFriends();
    refetchUnread();
  }, [refetchFriends, refetchUnread]);

  // --------------------------- Cross-tab / cross-component signals --------
  useEffect(() => {
    const fh = () => refetchFriends();
    const uh = () => refetchUnread();
    window.addEventListener("yappo-friends-changed", fh);
    window.addEventListener("yappo-conversations-changed", uh);
    return () => {
      window.removeEventListener("yappo-friends-changed", fh);
      window.removeEventListener("yappo-conversations-changed", uh);
    };
  }, [refetchFriends, refetchUnread]);

  // --------------------------- Toasts -------------------------------------
  const dismissToast = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const pushToast = useCallback((toast) => {
    idRef.current += 1;
    const id = idRef.current;
    setToasts((prev) => [{ ...toast, id }, ...prev].slice(0, 3));
    setTimeout(() => dismissToast(id), 7000);
    try {
      if (typeof Notification !== "undefined" && Notification.permission === "granted") {
        const n = new Notification(toast.title, {
          body: toast.subtitle,
          icon: "/logo192.png",
          tag: `social-${id}`,
        });
        n.onclick = () => { window.focus(); n.close(); };
        setTimeout(() => { try { n.close(); } catch {} }, 6000);
      }
    } catch {}
  }, [dismissToast]);

  // --------------------------- WS event handler ---------------------------
  const onMessage = useCallback((data) => {
    if (!data || !data.type) return;
    const t = data.type;

    if (t === "friend_request" && data.from) {
      playDing();
      pushToast({
        type: "request",
        userId: data.from.id,
        title: "🤝 Arkadaşlık isteği",
        subtitle: `${data.from.name} sana istek gönderdi`,
        actionLabel: "İncele",
        target: "/app/friends",
      });
      refetchFriends();
    } else if (t === "friend_accepted" && data.by) {
      playDing();
      pushToast({
        type: "accepted",
        userId: data.by.id,
        title: "🎉 Arkadaşlık kabul edildi",
        subtitle: `${data.by.name} isteğini kabul etti`,
        actionLabel: "Profil",
        target: `/app/user/${data.by.id}`,
      });
      refetchFriends();
    } else if (t === "friend_declined") {
      refetchFriends();
    } else if (t === "message" && data.message) {
      const msg = data.message;
      if (msg.anon_room_id) return;
      if (msg.sender_id === user?.id) return;
      if (msg.conversation_id === activeConvIdRef.current) return;
      setUnreadByConv((prev) => ({
        ...prev,
        [msg.conversation_id]: (prev[msg.conversation_id] || 0) + 1,
      }));
    } else if (t === "read") {
      // Fires when any participant marks read; if it's me, drop the badge.
      if (data.user_id === user?.id) {
        setUnreadByConv((prev) => {
          if (!prev[data.conversation_id]) return prev;
          const next = { ...prev };
          delete next[data.conversation_id];
          return next;
        });
      }
    } else if (t === "announcement" && data.text) {
      // Admin broadcast — surface as a persistent toast (10s) with a bullhorn.
      playDing();
      pushToast({
        type: "announcement",
        title: "📣 Yappo Duyuru",
        subtitle: String(data.text).slice(0, 400),
      });
    } else if (t === "blocked") {
      // Server told us this account is blocked — force the user out immediately.
      try {
        alert(data.reason || "Hesabınız askıya alındı.");
      } catch {}
      try {
        window.location.href = "/login";
      } catch {}
    }
  }, [user?.id, pushToast, refetchFriends]);

  useChatSocket({ enabled: !!user, onMessage });

  const totalUnreadMessages = Object.values(unreadByConv).reduce((s, v) => s + v, 0);

  return (
    <SocialContext.Provider
      value={{
        pendingRequestCount,
        totalUnreadMessages,
        unreadByConv,
        setActiveConversation,
        refetchUnread,
        refetchFriends,
        toasts,
        dismissToast,
      }}
    >
      {children}
    </SocialContext.Provider>
  );
}

export function useSocial() {
  const ctx = useContext(SocialContext);
  if (!ctx) throw new Error("useSocial must be used inside SocialProvider");
  return ctx;
}
